import React, { useCallback, useMemo, useRef, useState } from 'react';
import { saveAs } from 'file-saver';

import ExcelJS, { Row, Style } from 'exceljs';
import csNames from 'classnames';
import _ from 'lodash';

import { Column, Project } from '../../../utils/models';
import { formatTime, getChunkedData, getExtractedInspectionName, getInitials, getMaximumDate, getMinimumDate, matchPhrases, transformPageRange } from '../../../utils/utilities';
import { getAffirmativeOptions } from '../../../utils/data';
import { getCurrentUser } from '../../../services/authService';
import { APP_DATE_FORMAT, PAGE_SIZE_MAX, USER_TYPES } from '../../../utils/constants';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomPDFExport from '../pdf-export';
import LoadingBadge from '../../themed/loading-badge';
import Table from '../../common/table';
import ProjectStatusButton from '../../themed/project-status-button';
import Select from '../../themed/select/select';

import classNames from './style.module.scss';
import russelsmithLogo from '../../../assets/images/logo.png';

interface Props {
	projects: Project[];
	isLoading: boolean;
}

const ReportOverview: React.FC<Props> = ({ isLoading, projects }) => {
	const [isConfirmVisible, setConfirmVisible] = useState(false);
	const [isStatusIncluded, setStatusIncluded] = useState('no');
	const [isAcceptedByIncluded, setAcceptedByIncluded] = useState('no');
	const [isSubmittedDateIncluded, setSubmittedDateIncluded] = useState('yes');
	const [isCompletionDateIncluded, setCompletionDateIncluded] = useState('no');
	const [pageSize, setPageSize] = useState(25);

	const user = getCurrentUser();
	const ref = useRef<HTMLDivElement>(null);
	const timer = useRef<NodeJS.Timeout>();

	const handleToggleStatusInclusion = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setStatusIncluded(event.currentTarget.value);
	};
	
	const handleToggleAcceptedByInclusion = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setAcceptedByIncluded(event.currentTarget.value);
	};
	
	const handleToggleSubmittedDate = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSubmittedDateIncluded(event.currentTarget.value);
	};

	const handleToggleCompletionDate = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setCompletionDateIncluded(event.currentTarget.value);
	};

	const projectsWithSerialNumber = useMemo(() => {
		return _.orderBy(projects, ['lastUpdatedAt'], 'asc').map((project, index) => ({ ...project, srNo: index + 1 }));
	}, [projects]);

	const [organizationLogo, organizationName] = useMemo(() => {
		if (projects.length === 0) return [null, null]

		const isSameOrganization = projects.every((project) => project.organisationName === projects[0].organisationName);
		if (isSameOrganization) return [projects[0].organisationLogo, projects[0].organisationName];

		return [null, null];
	}, [projects]);

	const [minimumDate, maximumDate] = useMemo(() => {
		const submissionDates = projects.map((project) => formatTime(project.lastUpdatedAt, 'YYYY-MM-DD'));
		return [getMinimumDate(submissionDates), getMaximumDate(submissionDates)];
	}, [projects]);

	const fileName = useMemo(() => {
		return minimumDate && maximumDate && minimumDate === maximumDate ? `${minimumDate} to ${maximumDate}` : 'Report Transmittal';
	}, [minimumDate, maximumDate]);

	const columns = useMemo(() => {
		const columns: Column[] = [
			{
				path: 'srNo',
				label: 'Sr. No.',
			},
			{
				path: 'documentNumber',
				label: 'Document No.',
			},
			{
				path: 'revisionNumber',
				label: 'Revision Number',
			},
			{
				path: 'equipmentTag',
				label: 'Equipment Tag.',
			},
			{
				path: 'inspectionTypeName',
				label: 'Inspection Type',
				content: (project: Project) => (project.inspectionTypeName && project.organisationName) ? getExtractedInspectionName(project.inspectionTypeName) : ''
			},
			{
				path: 'module',
				label: 'Module',
			},
			{
				path: 'facilityName',
				label: 'Facility',
			},
			{
				path: 'reportInspectionData.creatorName',
				label: 'Inspected By',
				content: (project: Project) => project.reportInspectionData?.creatorName ?? project.supervisorName
			},
			{
				path: 'reportInspectionData.internalReviewerName',
				label: 'Report Reviewed By (RusselSmith)',
			},
		];

		if (matchPhrases(isCompletionDateIncluded, 'yes')) {
			const completedDateColumn = 			{
				path: 'firstSubmissionDate',
				label: 'Inspection Completion Date',
				content: (project: Project) => project.firstSubmissionDate ? formatTime(project.firstSubmissionDate, APP_DATE_FORMAT) : ''
			};

			columns.push(completedDateColumn);
		}

		if (matchPhrases(isSubmittedDateIncluded, 'yes')) {
			const submittedDateColumn = {
				path: 'updatedAt',
				label: 'Submission Date',
				content: (project: Project) => formatTime(project.updatedAt, APP_DATE_FORMAT),
			};

			columns.push(submittedDateColumn);
		}

		if (matchPhrases(isAcceptedByIncluded, 'yes')) {
			const acceptedByColumn = {
				path: 'reportInspectionData.acceptedByName',
				label: 'Report Reviewed & Approved By (MPN)',
				content: (project: Project) => <span>{project.reportInspectionData.acceptedByName ?? project.contactPersonName}</span>
			};

			columns.push(acceptedByColumn);
		}

		if (matchPhrases(isStatusIncluded, 'yes')) {
			const statusColumn = {
				path: 'projectStatus',
				label: 'Status',
				content: (project: Project) => <ProjectStatusButton status={project.projectStatus} isActive />
			};

			columns.push(statusColumn);
		}

		return columns;
	}, [isStatusIncluded, isCompletionDateIncluded, isAcceptedByIncluded, isSubmittedDateIncluded]);

	const appendStyleToRow = useCallback((row: Row) => {
		const style = {
			border: {
				top: { style: "thin", color: { argb: "000000" } },
				left: { style: "thin", color: { argb: "000000" } },
				bottom: { style: "thin", color: { argb: "000000" } },
				right: { style: "thin", color: { argb: "000000" } },
			},
			fill: {
				type: "pattern",
				pattern: "darkVertical",
				fgColor: { argb: "FFFF00" },
			},
			font: {
				name: "Futura",
				family: 4,
				size: 8,
				bold: true,
			}
		} as Style;

		const cell = row.getCell(1);
		row.height = 30;

		cell.border = style.border;
		cell.fill = style.fill;
		cell.font = style.font;
	}, []);

	const getProjectExcelRowData = useCallback((project: typeof projectsWithSerialNumber[0]) => {
		return columns.map((column) => {
			if (column.path === 'projectStatus') {
				return _.get(project, column.path);
			}
			
			return column.content ? column.content(project) : _.get(project, column.path)
		});
	}, [columns]);

	const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setPageSize(parseInt(e.target.value, 10));
	};

	const handleExcelExport = useCallback(async () => {
		if (!ref.current) return;

		const workbook = new ExcelJS.Workbook();
		const sheet = workbook.addWorksheet('Report Transmittal', { pageSetup: { orientation: 'portrait' }});
		
		sheet.addTable({
			name: 'report-transmittal-id',
			ref: 'A1',
			headerRow: true,
			totalsRow: true,
			columns: columns.map((column) => ({ name: column.label, totalsRowLabel: 'Totals', filterButton: true })),
			rows: projectsWithSerialNumber.map((project) => getProjectExcelRowData(project)),
			style: {
				theme: 'TableStyleLight10',
				showRowStripes: true,
				showColumnStripes: true,
			},
		});

		let row = sheet.addRow([`${user?.designation}: ${user?.firstName} ${user?.lastName}`, '']);
		appendStyleToRow(row);

		row = sheet.addRow(['Programmatics NDE Advisor', '']);
		appendStyleToRow(row);

		const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {type: 'application/octet-stream'});

		await sheet.protect(process.env.REACT_APP_EXCEL_PASSWORD as string, { });
		saveAs(blob, fileName + '.xlsx');
	}, [columns, projectsWithSerialNumber, appendStyleToRow, getProjectExcelRowData, user, fileName]);

	const getCustomerRole = useCallback(() => {
		const defaultCustomerRole = 'Approved By';
		const isSameOrganization = projects.every((project) => project.organisationId === projects[0].organisationId);
		
		if (isSameOrganization) {
			const organizationName = projects[0].organisationName;
			const customerRolePrefix = organizationName.length > 10 ? getInitials(organizationName) : organizationName;

			return `${customerRolePrefix} ${defaultCustomerRole}`;
		}

		return defaultCustomerRole;
	}, [projects]);

	return (
		<>
			<div className="p-3 pb-0">
				<div className="row">
					<div className="col">
						<Select 
							name='status'
							options={getAffirmativeOptions()} 
							label='Include Status' 
							placeholder='Include Status?' 
							value={isStatusIncluded} 
							onChange={handleToggleStatusInclusion}
						/>
					</div>
					
					<div className="col">
						<Select
							label='Select Items Per Page'
							name="page-size"
							placeholder={'Showing (' + pageSize + 'rows)'}
							options={transformPageRange(PAGE_SIZE_MAX)}
							value={pageSize}
							onChange={handlePageSizeChange}
						/>
					</div>

					<div className="col">
						<Select 
							name='accepted-by'
							options={getAffirmativeOptions()} 
							label='Include Reviewed By (MPN)' 
							placeholder='Include Reviewed By (MPN)?' 
							value={isAcceptedByIncluded} 
							onChange={handleToggleAcceptedByInclusion}
						/>
					</div>

					<div className="col">
						<Select 
							name='submitted-date'
							options={getAffirmativeOptions()} 
							label='Include Submitted Date' 
							placeholder='Include Submitted Date?' 
							value={isSubmittedDateIncluded} 
							onChange={handleToggleSubmittedDate}
						/>
					</div>
					
					<div className="col">
						<Select 
							name='completion-date'
							options={getAffirmativeOptions()} 
							label='Include Completion Date' 
							placeholder='Include Completion Date?' 
							value={isCompletionDateIncluded} 
							onChange={handleToggleCompletionDate}
						/>
					</div>
				</div>
			</div>

			
			{isConfirmVisible && (
				<div className="mt-1 px-3">
					<LoadingBadge message="Downloading..." />
				</div>
			)}

			<CustomPDFExport customPDFOptions={{ paperSize: 'A3' }} fileName={fileName}>
				{(params) => (
					<>
						<div className="container-fluid p-3">
							<Conditional isVisible={!isConfirmVisible}>
								<div className="d-inline-block mb-3">
									{params.isPublishing ? (
										<LoadingBadge message="Downloading..." />
									) : (
										<>
											<Button
												label="Download as PDF"
												icon='file-pdf-fill'
												style={{ marginTop: 0, marginRight: '1rem' }}
												onClick={() => {
													if (timer.current) clearTimeout(timer.current);
													
													setConfirmVisible(true);
													
													timer.current = setTimeout(async () => {
														await params.onDownload();
														
														setConfirmVisible(false);
														clearTimeout(timer.current);
													}, 3000);
												}}
											/>
										
											<Button
												label="Download as Excel File"
												icon='file-spreadsheet-fill'
												style={{ marginTop: 0, backgroundColor: 'green' }}
												onClick={() => handleExcelExport()}
											/>
										</>
									)}
								</div>
							</Conditional>

							<div className={classNames.countCover}>
								<div className={classNames.countCoverLogo}>
									<img src={russelsmithLogo} alt="Arridex" />
								</div>

								<div className={classNames.countCoverTitleBox}>
									<h2>
										Report Overview ({projectsWithSerialNumber.length} Reports)
									</h2>
								</div>
							</div>
						</div>

						{getChunkedData(projectsWithSerialNumber, pageSize).map((chunk, index) => (
							<div className='m-4 pdf-page' key={index}>
								<Conditional isVisible={!!organizationName}>
									<div className={classNames.customSectionLogos}>
										<Conditional isVisible={!!organizationLogo}>
											<div className={classNames.customSectionLogosBox}>
												<img
													src={organizationLogo ?? ''}
													alt={organizationName ?? ''}
													className={classNames.customSectionLogosItem}
												/>
											</div>
										</Conditional>
										
										<div className={classNames.customSectionLogosBoxMain}>
											Report Transmittal ({minimumDate} to {maximumDate})
										</div>

										<div className={classNames.customSectionLogosBox}>
											<img
												src={russelsmithLogo}
												alt="Arridex"
												className={classNames.customSectionLogosItem}
											/>
										</div>
									</div>
								</Conditional>

								<div 
									ref={ref}
									className={csNames({
										"p-2 bg-light border rounded table-responsive": true,
										[classNames.table]: true,
									})}
								>
									<Table
										columns={columns}
										data={chunk}
										sortColumn={{ path: 'updatedAt', order: 'desc' }}
										onSort={() => null}
										isFetching={isLoading}
									/>
								</div>
							</div>
						))}

						<Conditional isVisible={user?.userType === USER_TYPES.INTERNAL}>
							<div className={classNames.signatory}>
								<div className={classNames.signatoryField}>
									<div className={classNames.signatoryFieldColumn}>
										Submitted By
										<br />
										{user?.firstName} {user?.lastName}
									</div>
									<div className={classNames.signatoryFieldColumn}>
										<img src={user?.signature} alt={user?.firstName} />
									</div>
								</div>

								<div className={classNames.signatoryField}>
									{_.uniqBy(projectsWithSerialNumber, 'inspectionTypeName').map((project, index) => `${index + 1}. ${project.inspectionTypeName}`).join(' ')}
								</div>

								<div className={classNames.signatoryField}>
									<div className={classNames.signatoryFieldColumn}>
										{getCustomerRole()}
									</div>
									<div className={classNames.signatoryFieldColumn}>
									</div>
								</div>
							</div>
						</Conditional>
					</>
				)}
			</CustomPDFExport>
		</>
	);
};



export default ReportOverview;
